import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, X, Filter, Building2, Home, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import LoadingSpinner from '../ui/LoadingSpinner';

interface Profile {
  user_id: string;
  sales_name: string | null;
  full_name: string | null;
  squad_id: string | null;
  email?: string | null;
  role?: string;
}

interface Squad {
  id: string;
  name: string;
  color: string | null;
}

interface Client {
  id: string;
  name: string;
  company_name?: string;
}

interface ScheduleDay {
  id: string;
  date: string;
  day_type: string;
  client_id: string | null;
  sdr_id: string | null;
  squad_id: string | null;
  status: string;
  work_location?: string | null;
  client?: Client;
  sdr?: Profile;
  squad?: Squad;
}

interface PlanningViewProps {
  initialMonth: Date;
  sdrs: Profile[];
  squads: Squad[];
  clients: Client[];
  onAddDay: () => void;
  onCellClick: (sdrId: string, date: Date, schedule?: ScheduleDay) => void;
}

interface WeekInfo {
  weekNumber: number;
  startDate: Date;
  endDate: Date;
  dates: Date[];
  isMultiMonth: boolean;
  prospectionDays: number;
}

const getLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isSameDate = (date1: Date, date2: Date): boolean => {
  return date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate();
};

const isDateInCurrentWeek = (date: Date): boolean => {
  const today = new Date();
  const todayWeek = getISOWeek(today);
  const dateWeek = getISOWeek(date);
  return todayWeek === dateWeek && today.getFullYear() === date.getFullYear();
};

const getISOWeek = (date: Date): number => {
  const tempDate = new Date(date.valueOf());
  tempDate.setHours(0, 0, 0, 0);
  tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
  const week1 = new Date(tempDate.getFullYear(), 0, 4);
  return 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
};

const getWeeksInMonth = (year: number, month: number): WeekInfo[] => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const firstMonday = new Date(firstDay);
  const dayOfWeek = firstDay.getDay();
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  firstMonday.setDate(firstDay.getDate() + daysToMonday);

  const weeks: WeekInfo[] = [];
  let currentMonday = new Date(firstMonday);

  while (currentMonday <= lastDay || currentMonday.getMonth() === month) {
    const weekEnd = new Date(currentMonday);
    weekEnd.setDate(currentMonday.getDate() + 4);

    const weekDates: Date[] = [];
    for (let i = 0; i < 5; i++) {
      const day = new Date(currentMonday);
      day.setDate(currentMonday.getDate() + i);
      weekDates.push(day);
    }

    const isMultiMonth = weekDates.some(d => d.getMonth() !== month);

    weeks.push({
      weekNumber: getISOWeek(currentMonday),
      startDate: new Date(currentMonday),
      endDate: weekEnd,
      dates: weekDates,
      isMultiMonth,
      prospectionDays: 0
    });

    currentMonday.setDate(currentMonday.getDate() + 7);

    if (currentMonday.getMonth() > month && currentMonday > lastDay) {
      break;
    }
  }

  return weeks;
};

const PlanningView: React.FC<PlanningViewProps> = ({
  initialMonth,
  sdrs,
  squads,
  clients,
  onAddDay,
  onCellClick
}) => {
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [selectedSquad, setSelectedSquad] = useState<string>('all');
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const [schedules, setSchedules] = useState<ScheduleDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDayData, setSelectedDayData] = useState<{
    date: Date;
    prospection: Array<{ sdr: Profile; client: Client | null }>;
    available: Profile[];
    unavailable: Array<{ sdr: Profile; type: string }>;
  } | null>(null);
  const [workLocationFilter, setWorkLocationFilter] = useState<string>('all');

  const eagleSquad = useMemo(() => {
    return squads.find(s => s.name === 'EAGLE');
  }, [squads]);

  const eagleSdrs = useMemo(() => {
    return sdrs.filter(sdr => {
      const sdrSquad = squads.find(s => s.id === sdr.squad_id);
      return sdrSquad?.name === 'EAGLE';
    });
  }, [sdrs, squads]);

  const weeks = useMemo(() => {
    const weeksData = getWeeksInMonth(selectedMonth.getFullYear(), selectedMonth.getMonth());
    return weeksData.map(week => ({
      ...week,
      prospectionDays: week.dates.reduce((count, date) => {
        const dateStr = getLocalDateString(date);
        const daySchedules = schedules.filter(s => {
          if (s.date !== dateStr || s.day_type !== 'prospection') return false;
          const sdrSquad = squads.find(sq => sq.id === s.squad_id);
          return sdrSquad?.name === 'EAGLE';
        });
        return count + (daySchedules.length > 0 ? 1 : 0);
      }, 0) * eagleSdrs.length
    }));
  }, [selectedMonth, schedules, eagleSdrs, squads]);

  const filteredSdrs = useMemo(() => {
    const sdrsWithSquad = sdrs.filter(sdr => sdr.squad_id !== null && sdr.squad_id !== undefined);
    const filtered = selectedSquad === 'all' ? sdrsWithSquad : sdrsWithSquad.filter(sdr => sdr.squad_id === selectedSquad);

    return filtered.sort((a, b) => {
      const aSquad = squads.find(s => s.id === a.squad_id);
      const bSquad = squads.find(s => s.id === b.squad_id);
      const aIsBigBoss = aSquad?.name === 'BIG BOSS';
      const bIsBigBoss = bSquad?.name === 'BIG BOSS';

      if (aIsBigBoss && !bIsBigBoss) return 1;
      if (!aIsBigBoss && bIsBigBoss) return -1;

      const aSquadName = aSquad?.name || '';
      const bSquadName = bSquad?.name || '';
      if (aSquadName !== bSquadName) {
        return aSquadName.localeCompare(bSquadName);
      }

      return (a.full_name || a.sales_name || '').localeCompare(b.full_name || b.sales_name || '');
    });
  }, [sdrs, selectedSquad, squads]);

  const visibleDates = useMemo(() => {
    if (selectedWeek === null) {
      return weeks.flatMap(w => w.dates);
    }
    const week = weeks.find(w => w.weekNumber === selectedWeek);
    return week ? week.dates : [];
  }, [weeks, selectedWeek]);

  const stats = useMemo(() => {
    const datesToCount = visibleDates.map(d => getLocalDateString(d));
    const relevantSchedules = schedules.filter(s => {
      if (!datesToCount.includes(s.date)) return false;
      const sdrSquad = squads.find(sq => sq.id === s.squad_id);
      return sdrSquad?.name === 'EAGLE';
    });

    const prospection = relevantSchedules.filter(s => s.day_type === 'prospection').length;
    const unavailable = relevantSchedules.filter(s =>
      ['congé', 'malade', 'Jour Off', 'férié', 'formation'].includes(s.day_type)
    ).length;
    const totalSlots = datesToCount.length * eagleSdrs.length;
    const available = totalSlots - prospection - unavailable;

    return { prospection, unavailable, available };
  }, [visibleDates, schedules, eagleSdrs, squads]);

  useEffect(() => {
    loadSchedules();
  }, [selectedMonth]);

  useEffect(() => {
    const firstWeek = weeks[0];
    const lastWeek = weeks[weeks.length - 1];
    if (!firstWeek || !lastWeek) return;

    const startDate = getLocalDateString(firstWeek.startDate);
    const endDate = getLocalDateString(lastWeek.endDate);

    const scheduleChannel = supabase
      .channel('planning_schedule_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'prospecting_schedule',
          filter: `date=gte.${startDate},date=lte.${endDate}`
        },
        async (payload) => {
          if (payload.eventType === 'INSERT') {
            const { data } = await supabase
              .from('prospecting_schedule')
              .select(`
                *,
                client:clients(*),
                sdr:profiles!prospecting_schedule_sdr_id_fkey(*),
                squad:squads(*)
              `)
              .eq('id', payload.new.id)
              .single();

            if (data) {
              setSchedules(prev => [...prev, data]);
            }
          } else if (payload.eventType === 'UPDATE') {
            const { data } = await supabase
              .from('prospecting_schedule')
              .select(`
                *,
                client:clients(*),
                sdr:profiles!prospecting_schedule_sdr_id_fkey(*),
                squad:squads(*)
              `)
              .eq('id', payload.new.id)
              .single();

            if (data) {
              setSchedules(prev => prev.map(s => s.id === data.id ? data : s));
            }
          } else if (payload.eventType === 'DELETE') {
            setSchedules(prev => prev.filter(s => s.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(scheduleChannel);
    };
  }, [selectedMonth, weeks]);

  const loadSchedules = async () => {
    setLoading(true);
    try {
      const firstWeek = weeks[0];
      const lastWeek = weeks[weeks.length - 1];
      const startDate = getLocalDateString(firstWeek.startDate);
      const endDate = getLocalDateString(lastWeek.endDate);

      const { data, error } = await supabase
        .from('prospecting_schedule')
        .select(`
          *,
          client:clients(*),
          sdr:profiles!prospecting_schedule_sdr_id_fkey(*),
          squad:squads(*)
        `)
        .gte('date', startDate)
        .lte('date', endDate);

      if (error) throw error;
      setSchedules(data || []);
    } catch (error) {
      console.error('Error loading schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScheduleForSdrAndDate = (sdrId: string, date: Date): ScheduleDay | undefined => {
    const dateStr = getLocalDateString(date);
    return filteredByLocationSchedules.find(s => s.sdr_id === sdrId && s.date === dateStr);
  };

  const getCellStyle = (dayType: string, workLocation?: string | null): { bg: string; text: string; badge: string } => {
    switch (dayType) {
      case 'prospection':
        if (workLocation === 'remote') return { bg: 'bg-violet-50', text: 'text-violet-700', badge: 'bg-violet-500' };
        if (workLocation === 'other') return { bg: 'bg-teal-50', text: 'text-teal-700', badge: 'bg-teal-500' };
        return { bg: 'bg-green-50', text: 'text-green-700', badge: 'bg-green-500' };
      case 'formation':
        return { bg: 'bg-cyan-50', text: 'text-cyan-700', badge: 'bg-cyan-500' };
      case 'ecole':
        return { bg: 'bg-purple-50', text: 'text-purple-700', badge: 'bg-purple-500' };
      case 'maladie':
      case 'malade':
        return { bg: 'bg-red-50', text: 'text-red-700', badge: 'bg-red-500' };
      case 'congé':
        return { bg: 'bg-yellow-50', text: 'text-yellow-700', badge: 'bg-yellow-500' };
      case 'Jour Off':
        return { bg: 'bg-blue-50', text: 'text-blue-700', badge: 'bg-blue-500' };
      case 'goal volant':
        return { bg: 'bg-amber-50', text: 'text-amber-700', badge: 'bg-amber-500' };
      case 'hook closing':
        return { bg: 'bg-pink-50', text: 'text-pink-700', badge: 'bg-pink-500' };
      case 'hook sales':
        return { bg: 'bg-indigo-50', text: 'text-indigo-700', badge: 'bg-indigo-500' };
      case 'férié':
        return { bg: 'bg-red-50', text: 'text-red-700', badge: 'bg-red-600' };
      default:
        return { bg: 'bg-gray-50', text: 'text-gray-700', badge: 'bg-gray-500' };
    }
  };

  const getDayStats = (date: Date) => {
    const dateStr = getLocalDateString(date);
    const daySchedules = schedules.filter(s => {
      if (s.date !== dateStr) return false;
      const sdrSquad = squads.find(sq => sq.id === s.squad_id);
      return sdrSquad?.name === 'EAGLE';
    });
    const prospectionCount = daySchedules.filter(s => s.day_type === 'prospection').length;
    return { prospectionCount, total: eagleSdrs.length };
  };

  const handleDayClick = (date: Date) => {
    const dateStr = getLocalDateString(date);
    const daySchedules = schedules.filter(s => s.date === dateStr);

    const prospection = daySchedules
      .filter(s => s.day_type === 'prospection' && s.sdr)
      .map(s => ({ sdr: s.sdr!, client: s.client || null }));

    const unavailable = daySchedules
      .filter(s => s.day_type !== 'prospection' && s.sdr)
      .map(s => ({ sdr: s.sdr!, type: s.day_type }));

    const busySdrIds = new Set(daySchedules.map(s => s.sdr_id));
    const available = filteredSdrs.filter(sdr => !busySdrIds.has(sdr.user_id));

    setSelectedDayData({ date, prospection, available, unavailable });
  };

  const getSquadColor = (squadId: string | null): string => {
    if (!squadId) return '#94a3b8';
    const squad = squads.find(s => s.id === squadId);
    if (squad?.name === 'EAGLE') return '#3b82f6';
    if (squad?.name === 'WOLF') return '#ef4444';
    return squad?.color || '#94a3b8';
  };

  const getSquadBadgeClasses = (squadId: string | null): string => {
    if (!squadId) return 'bg-gray-100 text-gray-600';
    const squad = squads.find(s => s.id === squadId);
    if (squad?.name === 'EAGLE') return 'bg-blue-100 text-blue-700';
    if (squad?.name === 'WOLF') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-600';
  };

  const getInitials = (name: string | null): string => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const cycleWorkLocation = async (scheduleId: string, currentLocation: string | null | undefined) => {
    const cycle: Array<string | null> = [null, 'office', 'remote', 'other'];
    const currentIdx = cycle.indexOf(currentLocation || null);
    const nextValue = cycle[(currentIdx + 1) % cycle.length];

    setSchedules(prev => prev.map(s =>
      s.id === scheduleId ? { ...s, work_location: nextValue } : s
    ));

    const { error } = await supabase
      .from('prospecting_schedule')
      .update({ work_location: nextValue })
      .eq('id', scheduleId);

    if (error) {
      setSchedules(prev => prev.map(s =>
        s.id === scheduleId ? { ...s, work_location: currentLocation } : s
      ));
    }
  };

  const getWorkLocationIcon = (location: string | null | undefined) => {
    if (!location) return null;
    if (location === 'office') return (
      <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-amber-500 shadow-sm">
        <Building2 className="h-3 w-3 text-white" strokeWidth={2.5} />
      </span>
    );
    if (location === 'remote') return (
      <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-blue-500 shadow-sm">
        <Home className="h-3 w-3 text-white" strokeWidth={2.5} />
      </span>
    );
    if (location === 'other') return (
      <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-gray-500 shadow-sm">
        <MapPin className="h-3 w-3 text-white" strokeWidth={2.5} />
      </span>
    );
    return null;
  };

  const getWorkLocationLabel = (location: string | null | undefined) => {
    if (!location) return 'Non défini';
    if (location === 'office') return 'Bureau';
    if (location === 'remote') return 'Télétravail';
    if (location === 'other') return 'Autre';
    return 'Non défini';
  };

  const filteredByLocationSchedules = useMemo(() => {
    if (workLocationFilter === 'all') return schedules;
    if (workLocationFilter === 'none') return schedules.filter(s => !s.work_location);
    return schedules.filter(s => s.work_location === workLocationFilter);
  }, [schedules, workLocationFilter]);

  const formatDateHeader = (date: Date): string => {
    const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven'];
    return days[date.getDay() - 1] || '';
  };

  const formatWeekLabel = (week: WeekInfo): string => {
    const start = week.startDate;
    const end = week.endDate;

    if (start.getMonth() === end.getMonth()) {
      return `${start.getDate()}–${end.getDate()}`;
    } else {
      return `${start.getDate()}/${start.getMonth() + 1}–${end.getDate()}/${end.getMonth() + 1}`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Planning SDR</h2>
            <p className="text-sm text-gray-600 mt-1">
              {filteredSdrs.length} SDR{filteredSdrs.length > 1 ? 's' : ''} · {visibleDates.length} jours ouvrés
            </p>
          </div>

          <button
            onClick={onAddDay}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium shadow-sm"
          >
            <Plus className="h-5 w-5" />
            Nouvelle journée
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filtres</span>
          </div>
          <div className="flex items-center gap-2">
            {(() => {
              const availableSquads = squads.filter(s => {
                if (!['EAGLE', 'WOLF'].includes(s.name)) return false;
                return sdrs.some(sdr => sdr.squad_id === s.id);
              });

              return (
                <>
                  <button
                    key="all"
                    onClick={() => setSelectedSquad('all')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedSquad === 'all'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Tous les SDRs
                  </button>
                  {availableSquads.map((squad) => (
                    <button
                      key={squad.id}
                      onClick={() => setSelectedSquad(squad.id)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectedSquad === squad.id
                          ? squad.name === 'EAGLE'
                            ? 'bg-blue-500 text-white shadow-sm'
                            : 'bg-red-500 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {squad.name}
                    </button>
                  ))}
                </>
              );
            })()}

            <div className="w-px h-8 bg-gray-300 mx-2" />

            <button
              onClick={() => setWorkLocationFilter('all')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                workLocationFilter === 'all'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Tous
            </button>
            <button
              onClick={() => setWorkLocationFilter('office')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                workLocationFilter === 'office'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Building2 className="h-3.5 w-3.5" />
              Bureau
            </button>
            <button
              onClick={() => setWorkLocationFilter('remote')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                workLocationFilter === 'remote'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Home className="h-3.5 w-3.5" />
              Télétravail
            </button>
          </div>

          <div className="pt-4 border-t border-gray-200 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const newDate = new Date(selectedMonth);
                    newDate.setMonth(newDate.getMonth() - 1);
                    setSelectedMonth(newDate);
                    setSelectedWeek(null);
                  }}
                  className="p-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="px-6 py-2 bg-gray-50 border border-gray-200 rounded-lg font-semibold text-gray-900 min-w-[200px] text-center capitalize">
                  {selectedMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </div>
                <button
                  onClick={() => {
                    const newDate = new Date(selectedMonth);
                    newDate.setMonth(newDate.getMonth() + 1);
                    setSelectedMonth(newDate);
                    setSelectedWeek(null);
                  }}
                  className="p-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm font-medium text-green-700">{stats.prospection} prospection</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-sm font-medium text-red-700">{stats.unavailable} indispos</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-sm font-medium text-blue-700">{stats.available} libres</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="text-xs font-semibold text-gray-600 mb-2">LÉGENDE DES COULEURS</div>
              <div className="grid grid-cols-4 gap-2">
                <div className="flex items-center gap-1.5 text-xs">
                  <div className="w-3 h-3 rounded bg-green-500" />
                  <span className="text-gray-700 font-medium">Bureau</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <div className="w-3 h-3 rounded bg-violet-500" />
                  <span className="text-gray-700 font-medium">Télétravail</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <div className="w-3 h-3 rounded bg-teal-500" />
                  <span className="text-gray-700 font-medium">Autre lieu</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <div className="w-3 h-3 rounded bg-cyan-500" />
                  <span className="text-gray-700 font-medium">Formation</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <div className="w-3 h-3 rounded bg-red-500" />
                  <span className="text-gray-700 font-medium">Maladie</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <div className="w-3 h-3 rounded bg-yellow-500" />
                  <span className="text-gray-700 font-medium">Congé</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <div className="w-3 h-3 rounded bg-blue-500" />
                  <span className="text-gray-700 font-medium">Jour Off</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <div className="w-3 h-3 rounded bg-red-600" />
                  <span className="text-gray-700 font-medium">Férié</span>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-300 text-xs text-gray-600">
                💡 <span className="font-medium">Astuce:</span> Survolez une cellule pour voir les détails (SDR, client, date)
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm"
      >
        <div className="flex items-center gap-2 overflow-x-auto">
          <button
            onClick={() => setSelectedWeek(null)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              selectedWeek === null
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Mois complet
          </button>
          {weeks.map((week) => (
            <button
              key={week.weekNumber}
              onClick={() => setSelectedWeek(selectedWeek === week.weekNumber ? null : week.weekNumber)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                selectedWeek === week.weekNumber
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <span>S{weeks.indexOf(week) + 1}</span>
                <span className="text-xs opacity-75">· {formatWeekLabel(week)}</span>
                {week.isMultiMonth && <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />}
              </div>
              <div className={`text-xs mt-0.5 ${selectedWeek === week.weekNumber ? 'text-green-200' : 'text-green-600'}`}>
                {week.prospectionDays}p
              </div>
            </button>
          ))}
        </div>
      </motion.div>

      {['EAGLE', 'WOLF', 'BIG BOSS'].map(squadName => {
        const squad = squads.find(s => s.name === squadName);
        if (!squad) return null;

        const squadSdrs = filteredSdrs.filter(sdr => {
          const sdrSquad = squads.find(s => s.id === sdr.squad_id);
          return sdrSquad?.name === squadName;
        });

        if (squadSdrs.length === 0) return null;

        const totalAvailableDays = squadSdrs.reduce((sum, sdr) => {
          const allSchedules = schedules.filter(
            s => s.sdr_id === sdr.user_id &&
            new Date(s.date).getMonth() === selectedMonth.getMonth()
          );
          const workingDays = visibleDates.filter(d => d.getMonth() === selectedMonth.getMonth()).length;
          return sum + (workingDays - allSchedules.length);
        }, 0);

        return (
          <motion.div
            key={squadName}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
          >
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: getSquadColor(squad.id) }}
                  />
                  <h3 className="text-lg font-bold text-gray-900">Squad {squadName}</h3>
                  <span className="text-sm text-gray-600">· {squadSdrs.length} SDR{squadSdrs.length > 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-gray-600">Total disponible :</div>
                  <div className={`text-xl font-bold ${
                    totalAvailableDays <= 0 ? 'text-red-600' :
                    totalAvailableDays <= 5 ? 'text-orange-600' :
                    'text-green-600'
                  }`}>
                    {totalAvailableDays} jours
                  </div>
                </div>
              </div>
            </div>

        <div className="overflow-x-auto">
          <div className="min-w-max">
            <div className="flex border-b border-gray-200 bg-gray-50">
              <div className="w-[175px] flex-shrink-0 sticky left-0 bg-gray-50 z-20 border-r border-gray-200 p-4">
                <div className="text-sm font-semibold text-gray-700">SDR</div>
              </div>
            {visibleDates.map((date, idx) => {
              const stats = getDayStats(date);
              const isOutsideMonth = date.getMonth() !== selectedMonth.getMonth();
              const isToday = isSameDate(date, new Date());
              const isInCurrentWeek = isDateInCurrentWeek(date);
              const dateTooltip = `${date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}\n${stats.prospectionCount} SDR en prospection sur ${stats.total} disponibles\nCliquer pour voir le détail`;

              const getBgClass = () => {
                if (isToday) return 'bg-[#EE3195]';
                if (isInCurrentWeek) return 'bg-pink-50';
                return 'bg-gray-50';
              };

              const getTextClass = () => {
                if (isToday) return 'text-white';
                if (isInCurrentWeek) return 'text-gray-700';
                return 'text-gray-500';
              };

              const getDayTextClass = () => {
                if (isToday) return 'text-white';
                return 'text-gray-900';
              };

              const getStatsTextClass = () => {
                if (isToday) return 'text-white';
                return 'text-green-600';
              };

              return (
                <div
                  key={idx}
                  onClick={() => handleDayClick(date)}
                  title={dateTooltip}
                  className={`${selectedWeek === null ? 'w-[50px]' : 'w-[110px]'} flex-shrink-0 p-2 border-r border-gray-200 cursor-pointer hover:ring-2 hover:ring-pink-300 transition-all relative group ${
                    idx > 0 && weeks.some(w => w.dates[0].toISOString() === date.toISOString()) ? 'border-l-2 border-l-blue-300' : ''
                  } ${isOutsideMonth ? 'opacity-50' : ''} ${getBgClass()}`}
                >
                  <div className={`text-xs mb-1 font-medium ${getTextClass()}`}>{formatDateHeader(date)}</div>
                  <div className={`text-xl font-bold mb-2 flex items-center justify-center gap-1 ${getDayTextClass()}`}>
                    {isToday && <div className="w-2 h-2 rounded-full bg-white animate-pulse" />}
                    {isOutsideMonth ? `${date.getDate()}/${date.getMonth() + 1}` : date.getDate()}
                  </div>
                  <div className={`h-1 rounded-full overflow-hidden mb-1 ${isToday ? 'bg-white/30' : 'bg-gray-200'}`}>
                    <div
                      className={`h-full ${isToday ? 'bg-white' : 'bg-green-500'}`}
                      style={{ width: `${(stats.prospectionCount / stats.total) * 100}%` }}
                    />
                  </div>
                  <div className={`text-xs font-medium ${getStatsTextClass()}`}>{stats.prospectionCount}/{stats.total}</div>
                </div>
              );
            })}
            <div className="w-[60px] flex-shrink-0 sticky right-0 bg-gray-50 z-20 border-l border-gray-200 p-2">
              <div className="text-xs text-gray-700 font-semibold">Mois</div>
            </div>
          </div>

          {squadSdrs.map((sdr) => {
            const allSchedules = schedules.filter(
              s => s.sdr_id === sdr.user_id &&
              new Date(s.date).getMonth() === selectedMonth.getMonth()
            );
            const prospectionSchedules = allSchedules.filter(s => s.day_type === 'prospection');
            const prospectionCount = prospectionSchedules.length;
            const totalScheduledDays = allSchedules.length;
            const workingDays = visibleDates.filter(d => d.getMonth() === selectedMonth.getMonth()).length;
            const availableDays = workingDays - totalScheduledDays;
            const sdrSquadName = sdr.squad_id ? squads.find(s => s.id === sdr.squad_id)?.name || 'Aucune squad' : 'Aucune squad';
            const sdrTooltip = `${sdr.full_name || sdr.sales_name}\nSquad: ${sdrSquadName}\n${prospectionCount} jours de prospection ce mois\n${totalScheduledDays} jours affectés au total\n${availableDays} jours disponibles`;

            return (
              <div key={sdr.user_id} className="flex border-b border-gray-200 hover:bg-gray-50 transition-colors">
                <div
                  className="w-[175px] flex-shrink-0 sticky left-0 bg-white z-10 border-r border-gray-200 p-3 flex items-center gap-3 cursor-help"
                  title={sdrTooltip}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold shadow-sm"
                    style={{ backgroundColor: `${getSquadColor(sdr.squad_id)}15`, color: getSquadColor(sdr.squad_id) }}
                  >
                    {getInitials(sdr.full_name || sdr.sales_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{sdr.full_name || sdr.sales_name}</div>
                    <div className={`text-xs px-2 py-0.5 rounded inline-block ${getSquadBadgeClasses(sdr.squad_id)}`}>
                      {sdrSquadName}
                    </div>
                  </div>
                </div>
                {visibleDates.map((date, idx) => {
                  const schedule = getScheduleForSdrAndDate(sdr.user_id, date);
                  const style = schedule ? getCellStyle(schedule.day_type, schedule.work_location) : null;
                  const isOutsideMonth = date.getMonth() !== selectedMonth.getMonth();
                  const isToday = isSameDate(date, new Date());
                  const isInCurrentWeek = isDateInCurrentWeek(date);
                  const cellWidth = selectedWeek === null ? 'w-[50px]' : 'w-[110px]';
                  const maxChars = selectedWeek === null ? 5 : 14;

                  const getFullName = () => {
                    if (!schedule) return '';
                    if (schedule.day_type === 'prospection' && schedule.client) {
                      return schedule.client.company_name || schedule.client.name;
                    }
                    return schedule.day_type;
                  };

                  const getTooltipContent = () => {
                    if (!schedule) return 'Disponible - Cliquer pour affecter';

                    const parts = [
                      `SDR: ${sdr.full_name || sdr.sales_name}`,
                      `Type: ${schedule.day_type}`,
                    ];

                    if (schedule.day_type === 'prospection' && schedule.client) {
                      parts.push(`Client: ${schedule.client.company_name || schedule.client.name}`);
                    }

                    if (schedule.work_location) {
                      parts.push(`Lieu: ${getWorkLocationLabel(schedule.work_location)}`);
                    }

                    parts.push(`Date: ${date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}`);

                    return parts.join('\n');
                  };

                  const getCellBgClass = () => {
                    if (style) return `${style.bg} ${style.text}`;
                    if (isToday) return 'bg-pink-50';
                    if (isInCurrentWeek) return 'bg-pink-50/30';
                    return 'bg-white';
                  };

                  return (
                    <div
                      key={idx}
                      onClick={() => onCellClick(sdr.user_id, date, schedule)}
                      className={`${cellWidth} flex-shrink-0 p-2 border-r border-gray-200 cursor-pointer hover:ring-2 hover:ring-pink-300 transition-all relative group min-h-[56px] ${
                        idx > 0 && weeks.some(w => w.dates[0].toISOString() === date.toISOString()) ? 'border-l-2 border-l-blue-300' : ''
                      } ${isOutsideMonth ? 'opacity-50' : ''} ${getCellBgClass()}`}
                      title={schedule ? getFullName() : 'Disponible - Cliquer pour affecter'}
                    >
                      {schedule ? (
                        <div className="flex flex-col items-start gap-0.5">
                          <div className="flex items-center gap-0.5 w-full">
                            {style && <div className={`w-2 h-2 rounded-full ${style.badge} flex-shrink-0`} />}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                cycleWorkLocation(schedule.id, schedule.work_location);
                              }}
                              className="flex-shrink-0 p-0.5 rounded hover:bg-black/10 transition-colors"
                              title={`Lieu : ${getWorkLocationLabel(schedule.work_location)} (cliquer pour changer)`}
                            >
                              {schedule.work_location ? (
                                getWorkLocationIcon(schedule.work_location)
                              ) : (
                                <div className="w-4 h-4 rounded border-2 border-dashed border-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </button>
                          </div>
                          <div className="text-xs font-medium leading-tight line-clamp-2 break-words whitespace-normal w-full">
                            {schedule.day_type === 'prospection' && schedule.client
                              ? schedule.client.name
                              : schedule.day_type}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                        </div>
                      )}

                      <div className="absolute hidden group-hover:block z-50 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl whitespace-pre-line pointer-events-none
                        bottom-full left-1/2 -translate-x-1/2 mb-2 min-w-[200px]">
                        {getTooltipContent()}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  );
                })}
                <div className={`w-[60px] flex-shrink-0 sticky right-0 z-10 border-l border-gray-200 p-2 ${
                  availableDays <= 0 ? 'bg-red-100' : 'bg-white'
                }`}>
                  <div className="text-lg font-bold text-green-600">{prospectionCount}</div>
                  <div className="text-xs text-gray-500">/{workingDays}j</div>
                  <div className="mt-1 pt-1 border-t border-gray-300">
                    {availableDays <= 0 ? (
                      <div className="text-xs font-bold text-red-700">FULL</div>
                    ) : (
                      <div className={`text-xs font-semibold ${
                        availableDays <= 2 ? 'text-orange-600' : 'text-blue-600'
                      }`}>
                        {availableDays} dispo
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </motion.div>
        );
      })}

      {selectedDayData && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedDayData(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 capitalize">
                  {selectedDayData.date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  <span className="text-gray-500 font-normal ml-2">·</span>
                  <span className="text-green-600 ml-2">{selectedDayData.prospection.length} En Prospection</span>
                  <span className="text-gray-500 font-normal ml-2">·</span>
                  <span className="text-blue-600 ml-2">{selectedDayData.available.length} Dispo</span>
                  <span className="text-gray-500 font-normal ml-2">·</span>
                  <span className="text-red-600 ml-2">{selectedDayData.unavailable.length} Indispos</span>
                </h3>
                <button
                  onClick={() => setSelectedDayData(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>

            <div className="p-6 grid grid-cols-3 gap-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <span className="text-lg">✅</span>
                  Prospection ({selectedDayData.prospection.length})
                </h4>
                <div className="space-y-2">
                  {selectedDayData.prospection.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-green-100 shadow-sm">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shadow-sm"
                        style={{
                          backgroundColor: `${getSquadColor(item.sdr.squad_id)}15`,
                          color: getSquadColor(item.sdr.squad_id)
                        }}
                      >
                        {getInitials(item.sdr.full_name || item.sdr.sales_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">{item.sdr.full_name || item.sdr.sales_name}</div>
                        {item.client && (
                          <div className="text-xs text-green-700 px-2 py-0.5 bg-green-100 rounded inline-block mt-1">
                            {item.client.name}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-blue-700 mb-3 flex items-center gap-2">
                  <span className="text-lg">⬜</span>
                  Disponibles ({selectedDayData.available.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedDayData.available.map((sdr, idx) => (
                    <div
                      key={idx}
                      className="px-3 py-1.5 bg-white border border-blue-200 text-blue-700 rounded-lg text-sm font-medium shadow-sm"
                    >
                      {sdr.full_name || sdr.sales_name}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-2">
                  <span className="text-lg">🚫</span>
                  Indisponibles ({selectedDayData.unavailable.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedDayData.unavailable.map((item, idx) => (
                    <div
                      key={idx}
                      className="px-3 py-1.5 bg-white border border-red-200 text-red-700 rounded-lg text-sm shadow-sm"
                    >
                      <div className="font-medium">{item.sdr.full_name || item.sdr.sales_name}</div>
                      <div className="text-xs text-red-600 capitalize">{item.type}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm"
      >
        <h4 className="text-sm font-semibold text-gray-700 mb-4">Légende</h4>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full shadow-sm" />
            <span className="text-gray-700">Bureau</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-violet-500 rounded-full shadow-sm" />
            <span className="text-gray-700">Télétravail</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-teal-500 rounded-full shadow-sm" />
            <span className="text-gray-700">Autre lieu</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-cyan-500 rounded-full shadow-sm" />
            <span className="text-gray-700">Formation</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full shadow-sm" />
            <span className="text-gray-700">Maladie</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-full shadow-sm" />
            <span className="text-gray-700">Congé</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full shadow-sm" />
            <span className="text-gray-700">Jour Off</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-amber-500 rounded-full shadow-sm" />
            <span className="text-gray-700">Goal Volant</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-pink-500 rounded-full shadow-sm" />
            <span className="text-gray-700">Closing</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
            <span className="text-gray-600 text-xs">Semaine à cheval</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PlanningView;
